import { useEffect, useState } from 'preact/hooks'
import clsx from 'clsx'
import { fetchProductByIds } from 'libs/shopify-product'
import normalizeProducts from 'libs/shopify-normalize'
import { openModal } from 'helpers/dom'
import Icon from 'snippets/icon/icon'

const conversion = {
  g: 1,
  kg: 1000,
  ml: 1,
  l: 1000
}

const selectors = {
  SIZE_GUIDE_MODAL_SELECTOR: 'size-guide-modal'
}

const GRID_COLS_COUNT = 3

export default function ProductLinked({
  productIds,
  currentProductHandle,
  showLabel = true,
  classWrap = 'border border-neutral-900 flex justify-between'
}) {
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const convertSizeUnit = (str) => {
    const trimmed = str.trim()
    const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(.+)$/)

    if (!match) return Number.POSITIVE_INFINITY

    const value = parseFloat(match[1])
    const unit = match[2].trim().toLowerCase()

    return value * (conversion[unit] || 1)
  }

  const fetchProducts = async () => {
    try {
      setIsLoading(true)

      const response = await fetchProductByIds(productIds)
      const linkedProducts = normalizeProducts(response, 'shopify-graphql')
      const sortedProducts = linkedProducts
        .filter((product) => product.metafields.linked_size)
        .sort((a, b) => convertSizeUnit(a.metafields.linked_size) - convertSizeUnit(b.metafields.linked_size))

      setProducts(sortedProducts)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  const getBorderBottomCount = (itemsCount) => {
    const lastRowCount = itemsCount % GRID_COLS_COUNT || GRID_COLS_COUNT
    const borderBottomCount = itemsCount - lastRowCount

    return borderBottomCount
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  if (!Array.isArray(productIds) || productIds.length < 2) return null

  return (
    <div className={classWrap}>
      {showLabel && (
        <div className="flex items-center gap-1.5 px-4 py-2 flex-1 border-r border-neutral-900">
          <div className="text-xs text-neutral-900">{translate.product.size}</div>
          {window.sizeGuideState && (
            <button
              type="button"
              aria-label={translate.product.sizeGuide}
              onClick={() => {
                openModal(selectors.SIZE_GUIDE_MODAL_SELECTOR)
              }}
            >
              <Icon name="icon-info" viewBox="0 0 24 24" className="size-3.5 text-neutral-700" />
            </button>
          )}
        </div>
      )}
      <div className={clsx('grid', {
        'grid-cols-1': productIds.length === 1,
        'grid-cols-2': productIds.length === 2,
        'grid-cols-3': productIds.length > 2
      })}>
        {isLoading && productIds.map((id, index) => (
          <div
            key={id}
            className={clsx(
              'border-neutral-900 h-[47px] w-[72px] flex items-center justify-center animate-pulse border-r',
              {
                'border-b': index < getBorderBottomCount(productIds.length),
                '[&:nth-child(3n+3)]:border-r-0': index > 1,
                'last:border-r-0': index <= 1
              }
            )}
          >
            <div className="h-5 w-8 bg-skeleton" />
          </div>
        ))}
        {!isLoading &&
          products.map((product, index) => {
            const isCurrentProduct = product.handle === currentProductHandle

            if (!product.metafields.linked_size) return null

            return (
              <a
                key={product.id}
                href={`/products/${product.handle}`}
                className={clsx(
                  'border-neutral-900 border-r',
                  {
                    'border-b': index < getBorderBottomCount(products.length),
                    '[&:nth-child(3n+3)]:border-r-0': index > 1,
                    'last:border-r-0': index <= 1
                  }
                )}
              >
                <button
                  type="button"
                  className={clsx(
                    'text-xs px-1 py-2 text-neutral-900 h-12 w-full min-w-[72px] max-w-[100px]',
                    {
                      'text-white bg-neutral-900': isCurrentProduct,
                      'variant-selector-disabled': !product.available,
                      'active': !product.available && isCurrentProduct
                    }
                  )}
                >
                  {product.metafields.linked_size}
                </button>
              </a>
            )
          })}
      </div>
    </div>
  )
}
